// External packages
import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';

// Components
import { Button } from 'components/Button';
import { SelectCoordinatesMapField } from 'components/CoordinatesMap';
import { Form, InputField } from 'components/Form';
import { Label } from 'components/Forms';
import * as Modal from 'components/Modal';
import { Option } from 'components/Select';
import { SelectMultiple } from 'components/SelectMultiple';
import { Tab, TabList, TabPanel, Tabs } from 'components/Tabs';
import { TiptapEditorField } from 'components/TiptapEditor';

// Modules
import { AudioSelectField } from 'modules/AudioSelectField';
import { ImageSelectField } from 'modules/ImageSelectField';
import { PlaceSelectField } from 'modules/PlaceSelectField';
import { VideoSelectField } from 'modules/VideoSelectField';

// Schemas
import { exhibitsCreateSchema } from 'schemas/exhibit';

// Data
import languagesData from 'data/languages.json';

type ExhibitsModalProps = {
  onSubmit: (
    data: z.infer<typeof exhibitsCreateSchema>
  ) => Promise<void> | void;
  defaultValues?: z.infer<typeof exhibitsCreateSchema>;
  modalTrigger?: React.ReactNode;
  isPending?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export type Language = {
  langCode: string;
  langNativeName: string;
  langEnglishName: string;
};

export const ExhibitsModal: React.FC<ExhibitsModalProps> = ({
  onSubmit,
  defaultValues,
  modalTrigger,
  isPending,
  isOpen,
  onOpenChange,
}) => {
  const languages: Language[] = languagesData;

  const [selectedLanguages, setSelectedLanguages] = React.useState<Language[]>(
    () => {
      if (defaultValues && defaultValues.translations) {
        const langs = defaultValues.translations
          .map((t) => languages.find((l) => l.langCode === t.languageCode))
          .filter(Boolean) as Language[];

        return Array.from(new Set(langs.map((l) => l.langCode))).map(
          (code) => languages.find((l) => l.langCode === code)!
        );
      }

      return [languages[0]];
    }
  );

  const handleLanguageChange = (keys: unknown) => {
    let arr: string[] = Array.isArray(keys)
      ? keys.map(String)
      : keys instanceof Set
        ? Array.from(keys).map(String)
        : keys
          ? [String(keys)]
          : [];
    arr = Array.from(new Set(arr));

    if (arr.length === 0) {
      return;
    }

    const langs = arr
      .map((code) => languages.find((l) => l.langEnglishName === code))
      .filter(Boolean) as Language[];

    setSelectedLanguages(langs);
  };

  React.useEffect(() => {
    if (
      !defaultValues ||
      !defaultValues.translations?.some((t) =>
        selectedLanguages.some((l) => l.langCode === t.languageCode)
      )
    ) {
      return setSelectedLanguages([languages[0]]);
    }
  }, [defaultValues, languages]);

  return (
    <Modal.TriggerRoot isOpen={isOpen} onOpenChange={onOpenChange}>
      {modalTrigger}
      <Modal.Overlay isDismissable>
        <Modal.Root>
          <Modal.Dialog>
            <Modal.Close />
            <h3 className="mb-8 text-2xl text-gray-600">
              {defaultValues ? 'Uredi eksponat' : 'Dodaj eksponat'}
            </h3>
            <h3 className="mb-8 text-2xl text-gray-600"></h3>
            <Form
              schema={exhibitsCreateSchema}
              onSubmit={onSubmit}
              defaultValues={defaultValues}
            >
              <TranslationsCleaner selectedLanguages={selectedLanguages} />
              <InputField
                isRequired
                // isDisabled={!!defaultValues}
                name="name"
                inputProps={{ colorScheme: 'white' }}
                label="Naziv klase za vizualno prepoznavanje"
                className="flex-1 mb-4"
              />
              <div className="mb-4">
                <Label>Odaberi jezike</Label>
                <SelectMultiple
                  selectedKeys={selectedLanguages.map((l) => l.langEnglishName)}
                  onSelectionChange={handleLanguageChange}
                >
                  {languages &&
                    languages.map((lang) => (
                      <Option key={lang.langCode} id={lang.langEnglishName}>
                        {lang.langNativeName} ({lang.langEnglishName})
                      </Option>
                    ))}
                </SelectMultiple>
              </div>
              <Tabs className="mb-4">
                <TabList className="!justify-start">
                  {selectedLanguages.map((lang) => (
                    <Tab key={lang.langCode} id={lang.langEnglishName}>
                      {lang.langEnglishName}
                    </Tab>
                  ))}
                </TabList>
                {selectedLanguages.map((lang) => {
                  const index = selectedLanguages.findIndex(
                    (l) => l.langCode === lang.langCode
                  );

                  return (
                    <TabPanel
                      key={lang.langCode}
                      id={lang.langEnglishName}
                      className="px-4 py-6"
                    >
                      <AddValue index={index} langCode={lang.langCode} />
                      <InputField
                        isRequired
                        name={`translations.${index}.title`}
                        inputProps={{ colorScheme: 'white' }}
                        label={`Naslov (${lang.langEnglishName})`}
                        className="flex-1 mb-4"
                      />
                      <TiptapEditorField
                        name={`translations.${index}.shortDescription`}
                        editorProps={{ colorScheme: 'white' }}
                        label={`Kratak opis (${lang.langEnglishName})`}
                        className="flex-1 mb-4"
                      />
                      <TiptapEditorField
                        isRequired
                        name={`translations.${index}.description`}
                        editorProps={{ colorScheme: 'white' }}
                        label={`Opis (${lang.langEnglishName})`}
                        className="flex-1 mb-4"
                      />
                      <VideoSelectField
                        clearable
                        label={`Video (${lang.langEnglishName})`}
                        placeholder="Dodaj video"
                        containerClassName="mb-4"
                        name={`translations.${index}.videoIds`}
                      />
                      <AudioSelectField
                        clearable
                        label={`Audio (${lang.langEnglishName})`}
                        placeholder="Dodaj audio"
                        name={`translations.${index}.audioIds`}
                      />
                    </TabPanel>
                  );
                })}
              </Tabs>
              <ImageSelectField
                clearable
                label="Slika"
                placeholder="Dodaj sliku"
                containerClassName="mb-4"
                name="imageIds"
              />
              <PlaceSelectField
                isRequired
                label="Soba"
                placeholder="Dodaj sobu"
                name="placeId"
              />
              <SelectCoordinatesMapField
                isRequired
                label="Pozicija"
                name="position"
                wrapperClassName="mt-4"
              />
              <Button
                type="submit"
                className="w-full mt-12"
                size="lg"
                isPending={isPending}
              >
                {defaultValues ? 'Spremi promjene' : 'Dodaj eksponat'}
              </Button>
            </Form>
          </Modal.Dialog>
        </Modal.Root>
      </Modal.Overlay>
    </Modal.TriggerRoot>
  );
};

export const AddValue = ({
  index,
  langCode,
}: {
  index: number;
  langCode: string;
}) => {
  const { setValue } = useFormContext();

  React.useEffect(() => {
    setValue(`translations.${index}.languageCode`, langCode);
  }, [setValue]);

  return null;
};

export const TranslationsCleaner = ({
  selectedLanguages,
}: {
  selectedLanguages: Language[];
}) => {
  const { setValue, getValues } = useFormContext();

  React.useEffect(() => {
    const translations = getValues('translations') || [];
    const selectedCodes = selectedLanguages.map((l) => l.langCode);
    const filteredTranslations = translations.filter((t: any) =>
      selectedCodes.includes(t.languageCode)
    );
    if (filteredTranslations.length !== translations.length) {
      setValue('translations', filteredTranslations);
    }
  }, [selectedLanguages, setValue, getValues]);

  return null;
};
